import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Zap, Settings, Shield, Building2, Banknote, Users, FileText, 
  AlertTriangle, ClipboardList, LogOut, Camera, Brain, UserCog, Bell,
  ChevronRight, Menu, X, Wrench, TestTube, ChevronDown, ChevronUp,
  TrendingUp, Clock, CheckCircle, XCircle, Activity
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const AdminConsole = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showTestFunctions, setShowTestFunctions] = useState(false);
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [financialStats, setFinancialStats] = useState({ invoices: 0, payouts: 0, revenue: 0 });

  const fetchStats = useCallback(async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/admin/stats/overview`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setStats(data);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/admin/users`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setUsers(data.users || []);
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/admin/properties`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setProperties(data.properties || []);
    }
  }, []);

  const fetchRepairs = useCallback(async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/admin/repairs`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setRepairs(data.repairs || data || []);
    }
  }, []);

  const fetchDisputes = useCallback(async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/admin/disputes`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setDisputes(data.disputes || data || []);
    }
  }, []);

  const fetchAuditLog = useCallback(async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/admin/audit-log`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setAuditLog(data.logs || []);
    }
  }, []);

  const fetchFinancialData = useCallback(async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFinancialStats({
          invoices: data.pending_invoices || 0,
          payouts: data.pending_payouts || 0,
          revenue: data.monthly_revenue || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch financial data:', error);
    }
  }, []);

  const fetchRecentActivity = useCallback(async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/admin/audit-log?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRecentActivity(data.logs?.slice(0, 5) || []);
      }
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
    }
  }, []);

  // User management actions (actual database operations)
  const updateUserStatus = async (userId, newStatus) => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success(`User status updated to ${newStatus}`);
        fetchUsers();
      } else {
        toast.error('Failed to update user status');
      }
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const updateVerificationStatus = async (userId, kycStatus) => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/verification`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          kyc_status: kycStatus,
          aml_status: kycStatus === 'passed' ? 'passed' : undefined,
          mark_onboarding_complete: kycStatus === 'passed'
        })
      });
      if (response.ok) {
        toast.success('User verification status updated');
        fetchUsers();
      } else {
        toast.error('Failed to update verification status');
      }
    } catch (error) {
      toast.error('Failed to update verification status');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('User deleted successfully');
        fetchUsers();
      } else {
        toast.error('Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  useEffect(() => {
    fetchStats();
    fetchRecentActivity();
  }, [fetchStats, fetchRecentActivity]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'properties') fetchProperties();
    if (activeTab === 'finance') fetchFinancialData();
    if (activeTab === 'repairs') fetchRepairs();
    if (activeTab === 'disputes') fetchDisputes();
    if (activeTab === 'audit') fetchAuditLog();
  }, [activeTab, fetchUsers, fetchProperties, fetchFinancialData, fetchRepairs, fetchDisputes, fetchAuditLog]);

  // Quick Action Items - Only working production tools
  const quickActions = [
    { to: '/admin/agent-learning', icon: Brain, label: 'AI Learning', color: 'bg-purple-600' },
    { to: '/admin/cases', icon: ClipboardList, label: 'Cases', color: 'bg-blue-600' },
    { to: '/admin/arrears', icon: AlertTriangle, label: 'Arrears', color: 'bg-red-500' },
    { to: '/admin/finance/runs', icon: Banknote, label: 'Finance', color: 'bg-green-600' },
    { to: '/admin/config', icon: Settings, label: 'Config', color: 'bg-purple-600' },
    { to: '/admin/security', icon: Shield, label: 'Security', color: 'bg-orange-500' },
    { to: '/admin/impersonate', icon: UserCog, label: 'Impersonate', color: 'bg-pink-600' },
  ];

  // Test/Development tools - hidden by default
  const testActions = [
    { to: '/admin/simulation', icon: Zap, label: 'Integration Simulator', description: 'Test KYC, deposits, listings' },
    { to: '/admin/ai-review', icon: Brain, label: 'AI Review Queue', description: 'Review AI decisions' },
    { to: '/admin/ai-policy', icon: Shield, label: 'AI Policy Matrix', description: 'Configure AI rules' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-20 sm:pb-6">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b px-4 py-3 sm:px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg sm:text-2xl font-bold">Admin Console</h1>
            <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">LetWise Property Management</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="sm:hidden"
              onClick={() => setShowQuickActions(!showQuickActions)}
            >
              {showQuickActions ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <Button 
              onClick={handleLogout} 
              variant="outline" 
              size="sm"
              className="text-red-600 border-red-200"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Quick Actions Drawer */}
      {showQuickActions && (
        <div className="sm:hidden fixed inset-0 z-30 bg-black/50" onClick={() => setShowQuickActions(false)}>
          <div className="absolute right-0 top-0 h-full w-64 bg-white shadow-xl p-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold mb-4">Quick Actions</h3>
            <div className="space-y-2">
              {quickActions.map(({ to, icon: Icon, label, color }) => (
                <Link key={to} to={to} onClick={() => setShowQuickActions(false)}>
                  <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50">
                    <div className={`w-8 h-8 ${color} rounded-lg flex items-center justify-center`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-medium">{label}</span>
                    <ChevronRight className="w-4 h-4 ml-auto text-slate-400" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Desktop Quick Actions */}
        <div className="hidden sm:flex flex-wrap gap-2 mb-6">
          {quickActions.map(({ to, icon: Icon, label, color }) => (
            <Link key={to} to={to}>
              <Button className={`${color} hover:opacity-90`} size="sm">
                <Icon className="w-4 h-4 mr-1.5" />
                {label}
              </Button>
            </Link>
          ))}
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-1.5 mb-4 sm:mb-6 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          {['overview', 'users', 'properties', 'finance', 'repairs', 'disputes', 'audit'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium whitespace-nowrap rounded-lg transition-colors ${
                activeTab === tab
                  ? 'bg-primary text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-4">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <Card className="p-3 sm:p-4 border-l-4 border-l-blue-500">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-blue-500" />
                  <span className="text-xs sm:text-sm text-slate-600">Total Users</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold">{stats.users.total}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {stats.users.tenants} tenants • {stats.users.landlords} landlords
                </div>
              </Card>
              
              <Card className="p-3 sm:p-4 border-l-4 border-l-green-500">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-4 h-4 text-green-500" />
                  <span className="text-xs sm:text-sm text-slate-600">Properties</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold">{stats.properties.total}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {stats.properties.listed} listed • {stats.properties.let || 0} let
                </div>
              </Card>
              
              <Card className="p-3 sm:p-4 border-l-4 border-l-purple-500">
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="w-4 h-4 text-purple-500" />
                  <span className="text-xs sm:text-sm text-slate-600">Active Tenancies</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-green-600">{stats.tenancies.active}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {stats.tenancies.pending} pending approval
                </div>
              </Card>
              
              <Card className="p-3 sm:p-4 border-l-4 border-l-orange-500">
                <div className="flex items-center gap-2 mb-1">
                  <Wrench className="w-4 h-4 text-orange-500" />
                  <span className="text-xs sm:text-sm text-slate-600">Open Repairs</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-orange-600">{stats.repairs.open}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {stats.repairs.completed} completed
                </div>
              </Card>
            </div>
            
            {/* Status Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <Card className="p-4">
                <h3 className="text-sm sm:text-base font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500" />
                  Applications
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Submitted</span>
                    <span className="font-semibold">{stats.applications.submitted}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Shortlisted</span>
                    <span className="font-semibold text-green-600">{stats.applications.shortlisted}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Rejected</span>
                    <span className="font-semibold text-red-600">{stats.applications.rejected || 0}</span>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4">
                <h3 className="text-sm sm:text-base font-semibold mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  Disputes
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Open</span>
                    <span className="font-semibold text-red-600">{stats.disputes.open}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Under Review</span>
                    <span className="font-semibold text-yellow-600">{stats.disputes.under_review}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Resolved</span>
                    <span className="font-semibold text-green-600">{stats.disputes.resolved || 0}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <h3 className="text-sm sm:text-base font-semibold mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-indigo-500" />
                  Recent Activity
                </h3>
                <div className="space-y-2">
                  {recentActivity.length > 0 ? (
                    recentActivity.slice(0, 3).map((log, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span className="truncate">{log.action_type}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-slate-500">No recent activity</p>
                  )}
                </div>
              </Card>
            </div>

            {/* Mobile Quick Actions Grid */}
            <div className="sm:hidden">
              <h3 className="text-sm font-semibold mb-3">Quick Actions</h3>
              <div className="grid grid-cols-3 gap-2">
                {quickActions.slice(0, 6).map(({ to, icon: Icon, label, color }) => (
                  <Link key={to} to={to}>
                    <Card className="p-3 text-center hover:bg-slate-50">
                      <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center mx-auto mb-2`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-xs font-medium">{label}</span>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>

            {/* Test Functions - Collapsible */}
            <Card className="p-4 border-dashed border-2 border-slate-300 bg-slate-50/50">
              <button
                onClick={() => setShowTestFunctions(!showTestFunctions)}
                className="w-full flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-2">
                  <TestTube className="w-5 h-5 text-slate-500" />
                  <div>
                    <h3 className="font-semibold text-slate-700">Test Functions</h3>
                    <p className="text-xs text-slate-500">Development & testing tools</p>
                  </div>
                </div>
                {showTestFunctions ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              
              {showTestFunctions && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {testActions.map(({ to, icon: Icon, label, description }) => (
                      <Link key={to} to={to}>
                        <div className="p-3 rounded-lg border border-slate-200 hover:bg-white hover:shadow-sm transition-all">
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className="w-4 h-4 text-slate-600" />
                            <span className="font-medium text-sm">{label}</span>
                          </div>
                          <p className="text-xs text-slate-500">{description}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-3 text-center">
                    These tools are for testing and development purposes
                  </p>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-bold">User Management</h2>
              <span className="text-sm text-slate-500">{users.length} users</span>
            </div>
            <div className="space-y-3">
              {users.map(user => (
                <Card key={user.id} className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm sm:text-base truncate">
                          {user.first_name} {user.last_name}
                        </h3>
                        {user.status === 'active' && (
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-slate-600 truncate">{user.email}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                          {user.role}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          user.status === 'active' ? 'bg-green-100 text-green-800' :
                          user.status === 'suspended' ? 'bg-red-100 text-red-800' :
                          'bg-slate-100 text-slate-800'
                        }`}>
                          {user.status}
                        </span>
                        {user.kyc_status && (
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            user.kyc_status === 'passed' ? 'bg-green-100 text-green-800' :
                            user.kyc_status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            KYC: {user.kyc_status}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => updateUserStatus(user.id, user.status === 'active' ? 'suspended' : 'active')}
                      >
                        {user.status === 'active' ? 'Suspend' : 'Activate'}
                      </Button>
                      {user.role === 'landlord' && user.kyc_status !== 'passed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs bg-green-50 hover:bg-green-100"
                          onClick={() => updateVerificationStatus(user.id, 'passed')}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verify
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs text-red-600 hover:bg-red-50"
                        onClick={() => deleteUser(user.id)}
                      >
                        <XCircle className="w-3 h-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
              {users.length === 0 && (
                <Card className="p-8 text-center text-slate-500">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No users found</p>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Finance Tab */}
        {activeTab === 'finance' && (
          <div className="space-y-4">
            <h2 className="text-lg sm:text-xl font-bold">Financial Overview</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Card className="p-3 sm:p-4 border-l-4 border-l-blue-500">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 mb-1">
                  <FileText className="w-4 h-4" />
                  Pending Invoices
                </div>
                <div className="text-xl sm:text-2xl font-bold">{financialStats.invoices}</div>
              </Card>
              
              <Card className="p-3 sm:p-4 border-l-4 border-l-orange-500">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 mb-1">
                  <Banknote className="w-4 h-4" />
                  Awaiting Payouts
                </div>
                <div className="text-xl sm:text-2xl font-bold">{financialStats.payouts}</div>
              </Card>
              
              <Card className="p-3 sm:p-4 border-l-4 border-l-green-500">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  Monthly Revenue
                </div>
                <div className="text-xl sm:text-2xl font-bold text-green-600">
                  £{financialStats.revenue.toLocaleString()}
                </div>
              </Card>
            </div>
            
            <Card className="p-4">
              <h3 className="text-sm sm:text-base font-semibold mb-3">Financial Tools</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Link to="/admin/finance/runs">
                  <Button className="w-full justify-start text-xs sm:text-sm" variant="outline" size="sm">
                    <Banknote className="w-4 h-4 mr-2" />
                    Finance Runs
                  </Button>
                </Link>
                <Link to="/admin/finance/schedules">
                  <Button className="w-full justify-start text-xs sm:text-sm" variant="outline" size="sm">
                    <Clock className="w-4 h-4 mr-2" />
                    Payment Schedules
                  </Button>
                </Link>
                <Link to="/finance/deposits">
                  <Button className="w-full justify-start text-xs sm:text-sm" variant="outline" size="sm">
                    <Shield className="w-4 h-4 mr-2" />
                    Deposit Lifecycle
                  </Button>
                </Link>
                <Link to="/admin/scottish-deposits">
                  <Button className="w-full justify-start text-xs sm:text-sm" variant="outline" size="sm">
                    <FileText className="w-4 h-4 mr-2" />
                    Scottish Deposits
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        )}

        {/* Properties Tab */}
        {activeTab === 'properties' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-bold">All Properties</h2>
              <span className="text-sm text-slate-500">{properties.length} properties</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {properties.map(property => (
                <Card key={property.id} className="p-3 sm:p-4">
                  <h3 className="font-semibold text-sm sm:text-base mb-1">
                    {property.bedrooms} bed {property.property_type}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 mb-3 truncate">
                    {property.address?.line1}, {property.address?.city}
                  </p>
                  <div className="flex justify-between items-center">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      property.status === 'listed' ? 'bg-green-100 text-green-800' :
                      property.status === 'let' ? 'bg-blue-100 text-blue-800' :
                      'bg-slate-100 text-slate-800'
                    }`}>
                      {property.status}
                    </span>
                    {property.rent_amount && (
                      <span className="text-sm sm:text-base font-bold">£{property.rent_amount}/mo</span>
                    )}
                  </div>
                </Card>
              ))}
              {properties.length === 0 && (
                <Card className="p-8 text-center text-slate-500 col-span-2">
                  <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No properties found</p>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Repairs Tab */}
        {activeTab === 'repairs' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-bold">All Repairs</h2>
              <span className="text-sm text-slate-500">{repairs.length} repairs</span>
            </div>
            <div className="space-y-3">
              {repairs.map(repair => (
                <Card key={repair.id} className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm sm:text-base truncate">{repair.title}</h3>
                      <p className="text-xs sm:text-sm text-slate-600 mt-1 line-clamp-2">{repair.description}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          repair.severity === 'emergency' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {repair.severity}
                        </span>
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-800 rounded text-xs">
                          {repair.category}
                        </span>
                      </div>
                    </div>
                    <span className={`self-start px-2 py-1 rounded-full text-xs whitespace-nowrap ${
                      repair.status === 'completed' ? 'bg-green-100 text-green-800' :
                      repair.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {repair.status}
                    </span>
                  </div>
                </Card>
              ))}
              {repairs.length === 0 && (
                <Card className="p-8 text-center text-slate-500">
                  <Wrench className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No repairs found</p>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Disputes Tab */}
        {activeTab === 'disputes' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-bold">All Disputes</h2>
              <span className="text-sm text-slate-500">{disputes.length} disputes</span>
            </div>
            <div className="space-y-3">
              {disputes.map(dispute => (
                <Card key={dispute.id} className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm sm:text-base truncate">{dispute.title}</h3>
                      <p className="text-xs sm:text-sm text-slate-600 mt-1 line-clamp-2">{dispute.description}</p>
                      <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs mt-2">
                        {dispute.dispute_type}
                      </span>
                    </div>
                    <span className={`self-start px-2 py-1 rounded-full text-xs whitespace-nowrap ${
                      dispute.status === 'open' ? 'bg-red-100 text-red-800' :
                      dispute.status === 'under_review' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {dispute.status}
                    </span>
                  </div>
                  <Button variant="outline" size="sm" className="text-xs">
                    View Details
                  </Button>
                </Card>
              ))}
              {disputes.length === 0 && (
                <Card className="p-8 text-center text-slate-500">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No disputes found</p>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Audit Log Tab */}
        {activeTab === 'audit' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-bold">Audit Log</h2>
              <span className="text-sm text-slate-500">{auditLog.length} entries</span>
            </div>
            <Card className="p-3 sm:p-4">
              <div className="space-y-2">
                {auditLog.map((log, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-2 border-b last:border-b-0 gap-1">
                    <div className="text-sm">
                      <span className="font-medium">{log.action_type}</span>
                      <span className="text-slate-600 ml-1 text-xs sm:text-sm">
                        on {log.target_type}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                ))}
                {auditLog.length === 0 && (
                  <p className="text-center text-slate-500 py-4">No audit logs found</p>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminConsole;
